import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { Input } from '@highlight/ui'
import { formatDateField } from '../utils/format-date-field'
import { formatDateInput, parseDateInput } from '../utils/parse-date-input'

interface DateFieldProps {
  value?: Date
  onFocus: () => void
  onBlur?: () => void
  onChange: (date: Date) => void
}

export function DateField({ value, onFocus, onBlur, onChange }: DateFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isFocused, setIsFocused] = useState(false)
  const digitsRef = useRef('')

  useEffect(() => {
    if (!isFocused && inputRef.current) {
      inputRef.current.value = value ? formatDateField(value) : ''
    }
  }, [value, isFocused])

  const handleFocus = () => {
    setIsFocused(true)
    digitsRef.current = ''
    onFocus()
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // When backspace hits the auto-inserted '/', also remove the preceding digit
    if (e.key === 'Backspace' && inputRef.current) {
      const val = inputRef.current.value
      if (val.endsWith('/')) {
        e.preventDefault()
        const newVal = val.slice(0, -2)
        inputRef.current.value = newVal
        digitsRef.current = newVal.replace(/\D/g, '')
      }
    }
  }

  const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
    if (!inputRef.current) return
    if (digitsRef.current === '') {
      // First keystroke after focus — ignore pre-existing display value, start fresh
      inputRef.current.value = (e.nativeEvent as InputEvent).data ?? ''
    }
    const digits = inputRef.current.value.replace(/\D/g, '').slice(0, 4)
    digitsRef.current = digits
    const formatted = formatDateInput(digits)
    inputRef.current.value = formatted
    // Keep cursor at end
    inputRef.current.setSelectionRange(formatted.length, formatted.length)
  }

  const handleBlur = () => {
    setIsFocused(false)
    const parsed = parseDateInput(formatDateInput(digitsRef.current))
    if (parsed) {
      if (inputRef.current) inputRef.current.value = formatDateField(parsed)
      onChange(parsed)
    }
    // No typing → leave input empty; useEffect will fill it when value prop updates.
    digitsRef.current = ''
    onBlur?.()
  }

  return (
    <Input
      ref={inputRef}
      size="sm"
      type="text"
      inputMode="numeric"
      defaultValue={value ? formatDateField(value) : ''}
      placeholder="Add Date"
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      onInput={handleInput}
      onBlur={handleBlur}
      className="w-[120px] shrink-0"
    />
  )
}
